import { TrackerImporter } from "./tracker-importer";
import { CategoryValidator } from "../validators/category-validator";
import { EventValidator } from "../validators/event-validator";
import { ActionValidator } from "../validators/action-validator";
import { LabelValidator } from "../validators/label-validator";
import { PropertyValidator } from "../validators/property-validator";
import { ValueValidator } from "../validators/value-validator";
import { Product } from "../../enums/product";
import { ErrorMessage } from "../../enums/error-message";
import { EventName } from "../../enums/event-name";
import { ScholarshipCardTracker } from "./scholarship-card-tracker";
import { StudyCardTracker } from "./study-card-tracker";
import { PartnerIncentiveTracker } from "./partner-incentive-tracker";
import { UniversityCardTracker } from "./university-card-tracker";
import { ProductViewTracker } from "./product-view-tracker";
export class Tracker {
    product;
    dataLayerTracker;
    trackerImporter;
    structuredEventValidator;
    trackWithGoogleAnalytics;
    trackWithSnowplow;
    constructor(product, trackWithGoogleAnalyics = false, trackWithSnowplow = true) {
        this.product = product;
        this.dataLayerTracker = null;
        this.trackerImporter = null;
        this.structuredEventValidator = this.initStructuredEventValidators();
        this.trackWithGoogleAnalytics = trackWithGoogleAnalyics;
        this.trackWithSnowplow = trackWithSnowplow;
        this.initTrackerSupport();
    }
    trackStructuredEvent(trackingData, eventName = EventName.LEGACY_EVENT) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        trackingData['category'] = this.product;
        this.structuredEventValidator.validate(trackingData);
        this.dataLayerTracker.trackStructuredEvent({
            ...trackingData,
            trackWithGoogleAnalytics: this.trackWithGoogleAnalytics,
            trackWithSnowplow: this.trackWithSnowplow
        }, eventName);
    }
    trackExperimentEvent(trackingData) {
        trackingData.category = Product.EXPERIMENT;
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        if (this.dataLayerTracker !== null) {
            this.dataLayerTracker.trackStructuredEvent({
                ...trackingData,
                trackWithGoogleAnalytics: this.trackWithGoogleAnalytics,
                trackWithSnowplow: this.trackWithSnowplow
            }, EventName.EXPERIMENT_EVENT);
        }
    }
    trackStudyCardImpression(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new StudyCardTracker(this.dataLayerTracker).trackImpression(trackingData, {
            trackWithGoogleAnalytics: this.trackWithGoogleAnalytics,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackScholarshipCardImpression(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new ScholarshipCardTracker(this.dataLayerTracker).trackImpression(trackingData, {
            trackWithGoogleAnalytics: true,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackUniversityCardImpression(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new UniversityCardTracker(this.dataLayerTracker).trackImpression(trackingData, {
            trackWithGoogleAnalytics: this.trackWithGoogleAnalytics,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackUniversityCardClick(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new UniversityCardTracker(this.dataLayerTracker).trackClick(trackingData, {
            trackWithGoogleAnalytics: this.trackWithGoogleAnalytics,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackStudyCardClick(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new StudyCardTracker(this.dataLayerTracker).trackClick(trackingData, {
            trackWithGoogleAnalytics: true,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackScholarshipCardClick(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new ScholarshipCardTracker(this.dataLayerTracker).trackClick(trackingData, {
            trackWithGoogleAnalytics: true,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackProductView(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new ProductViewTracker(this.dataLayerTracker).trackImpression(trackingData, {
            trackWithGoogleAnalytics: this.trackWithGoogleAnalytics,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackPartnerIncentiveImpression(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new PartnerIncentiveTracker(this.dataLayerTracker).trackImpression(trackingData, {
            trackWithGoogleAnalytics: this.trackWithGoogleAnalytics,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackPartnerIncentiveClick(trackingData) {
        if (!this.dataLayerTracker) {
            throw new Error(ErrorMessage.DATA_LAYER_TRACKER_UNAVAILABLE);
        }
        new PartnerIncentiveTracker(this.dataLayerTracker).trackClick(trackingData, {
            trackWithGoogleAnalytics: this.trackWithGoogleAnalytics,
            trackWithSnowplow: this.trackWithSnowplow
        });
    }
    trackSiteSearch(trackingData) {
        if (typeof window['snowplow'] === 'undefined') {
            throw new Error('Snowplow not found!');
        }
        window['snowplow']('trackSiteSearch', trackingData);
    }
    initStructuredEventValidators() {
        const eventValidator = new EventValidator();
        eventValidator.addValidator(new CategoryValidator);
        eventValidator.addValidator(new ActionValidator);
        eventValidator.addValidator(new LabelValidator);
        eventValidator.addValidator(new PropertyValidator);
        eventValidator.addValidator(new ValueValidator);
        return eventValidator;
    }
    initTrackerSupport() {
        this.trackerImporter = new TrackerImporter();
        this.trackerImporter.loadTracker(() => {
            this.dataLayerTracker = this.trackerImporter.dataLayerTracker;
        });
        if (this.dataLayerTracker) {
            window['DatalakeEventTrackerLoaded'] = true;
            document.dispatchEvent(new Event('DatalakeEventTrackerLoaded'));
        }
    }
}
